import { useEffect, useState } from "react";
import { FloatingFounderBadge } from "./FounderBadge";

interface PenelopeEasterEggsProps {
  isFounder: boolean;
  currentStep?: number;
  isGenerating?: boolean;
}

export function PenelopeEasterEggs({ 
  isFounder, 
  currentStep, 
  isGenerating = false 
}: PenelopeEasterEggsProps) {
  const [showFloatingBadge, setShowFloatingBadge] = useState(false);
  const [showSpecialMessage, setShowSpecialMessage] = useState(false);

  useEffect(() => {
    if (isFounder) {
      // Show floating badge only during key moments (story generation or first visit)
      if (isGenerating) {
        const badgeInterval = setInterval(() => {
          setShowFloatingBadge(true);
          setTimeout(() => setShowFloatingBadge(false), 4000);
        }, 20000); // Less frequent, only during generation

        // Show special message on story generation
        setShowSpecialMessage(true);
        setTimeout(() => setShowSpecialMessage(false), 6000);

        return () => clearInterval(badgeInterval);
      }
    }
  }, [isFounder, isGenerating]);

  if (!isFounder) return null;

  return (
    <>
      {/* Floating founder badge */}
      {showFloatingBadge && <FloatingFounderBadge />}
      
      {/* Special story generation message for Penelope */}
      {showSpecialMessage && isGenerating && (
        <div className="fixed bottom-4 left-4 z-50 pointer-events-none">
          <div className="bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white p-4 rounded-lg shadow-lg max-w-xs animate-bounce">
            <div className="text-center">
              <p className="font-bold text-sm">🌟 Creating magic for our founder! 🌟</p>
              <p className="text-xs mt-1 opacity-90">
                Extra sparkles added just for you, Penelope! ✨
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Cute animated elements - only during story generation to avoid distraction */}
      {isGenerating && (
        <>
          <div className="fixed top-1/2 left-2 z-40 pointer-events-none">
            <div className="text-2xl animate-bounce" style={{ animationDelay: '0s' }}>🦄</div>
          </div>
          <div className="fixed top-1/3 right-2 z-40 pointer-events-none">
            <div className="text-2xl animate-bounce" style={{ animationDelay: '2s' }}>🌈</div>
          </div>
          <div className="fixed bottom-1/4 left-4 z-40 pointer-events-none">
            <div className="text-xl animate-pulse" style={{ animationDelay: '1s' }}>⭐</div>
          </div>
          <div className="fixed bottom-1/3 right-4 z-40 pointer-events-none">
            <div className="text-xl animate-pulse" style={{ animationDelay: '3s' }}>💫</div>
          </div>
        </>
      )}
    </>
  );
}

// Special welcome component for Penelope's first visit
export function PenelopeWelcome({ isFounder }: { isFounder: boolean }) {
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    if (isFounder) {
      // Check if this is the first visit today
      const lastVisit = localStorage.getItem('penelopeLastVisit');
      const today = new Date().toDateString();
      
      if (lastVisit !== today) {
        setShowWelcome(true);
        localStorage.setItem('penelopeLastVisit', today);
        
        // Auto-hide after 8 seconds
        setTimeout(() => setShowWelcome(false), 8000);
      }
    }
  }, [isFounder]);

  if (!isFounder || !showWelcome) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-pink-100 via-purple-100 to-blue-100 p-8 rounded-3xl shadow-2xl max-w-md mx-auto text-center animate-bounce">
        <div className="text-6xl mb-4">🎉</div>
        <h2 className="text-2xl font-bold text-purple-800 mb-4">
          Welcome back, Penelope! 🌟
        </h2>
        <p className="text-purple-600 mb-4">
          Thank you for creating this magical place where children's imaginations come to life! 
        </p>
        <p className="text-pink-600 text-sm font-semibold">
          ✨ Your vision makes bedtime stories extraordinary ✨
        </p>
        <button 
          onClick={() => setShowWelcome(false)}
          className="mt-6 px-6 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full hover:scale-105 transition-transform"
        >
          Let's create magic! 🪄
        </button>
      </div>
    </div>
  );
}